import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { ChevronDown, Check, Search } from "lucide-react";

/**
 * Searchable dropdown with keyboard navigation and viewport-aware popup.
 * - Options list has an explicit 220px max-height with a working scrollbar.
 * - Flips upward when there isn't enough room below the trigger.
 * - Arrow keys move highlight AND scroll the highlighted row into view.
 * - Wheel/touch events don't leak to the background page.
 * options: [{ value, label, sub? }]
 */
export default function SearchableSelect({
  value, onChange, options, placeholder = "Select...", testId, disabled,
  emptyMessage = "No options"
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(0);
  const [flipUp, setFlipUp] = useState(false);
  const ref = useRef(null);
  const buttonRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const optionRefs = useRef([]);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => {
    if (open) requestAnimationFrame(() => inputRef.current?.focus());
    else { setQuery(""); setHighlight(0); }
  }, [open]);

  // Decide direction before paint so the popup never briefly overflows the
  // viewport. If less than 300px of room exists below, open upward instead.
  useLayoutEffect(() => {
    if (!open || !buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    setFlipUp(spaceBelow < 300 && rect.top > spaceBelow);
  }, [open]);

  // Auto-scroll the highlighted option into view on arrow-key navigation.
  useEffect(() => {
    const el = optionRefs.current[highlight];
    if (el && el.scrollIntoView) el.scrollIntoView({ block: "nearest" });
  }, [highlight]);

  const q = query.trim().toLowerCase();
  const filtered = q ? options.filter(o => o.label.toLowerCase().includes(q) || (o.sub || "").toLowerCase().includes(q)) : options;
  const selected = options.find(o => o.value === value);

  const onKey = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlight(h => Math.min(filtered.length - 1, h + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight(h => Math.max(0, h - 1)); }
    else if (e.key === "Enter") {
      e.preventDefault();
      if (filtered[highlight]) { onChange(filtered[highlight].value); setOpen(false); }
    } else if (e.key === "Escape") { setOpen(false); }
    else if (/^[a-zA-Z0-9]$/.test(e.key) && !q) {
      const idx = options.findIndex(o => o.label.toLowerCase().startsWith(e.key.toLowerCase()));
      if (idx >= 0) setHighlight(idx);
    }
  };

  return (
    <div ref={ref} className="relative w-full">
      <button ref={buttonRef} type="button" disabled={disabled} data-testid={testId}
        onClick={() => {
          setOpen(o => !o);
          requestAnimationFrame(() => inputRef.current?.focus());
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen(true);
            requestAnimationFrame(() => inputRef.current?.focus());
          } else if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            e.preventDefault();
            setOpen(true);
            requestAnimationFrame(() => inputRef.current?.focus());
          }
        }}
        className={`w-full h-11 px-3 bg-white border ${open?"border-[#2D2622]":"border-[#E5D8C4]"} text-sm text-left flex items-center justify-between hover:border-[#8B6F47] transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}>
        <span className={selected ? "text-[#2D2622]" : "text-[#A8998A]"}>{selected?.label || placeholder}</span>
        <ChevronDown size={15} className={`text-[#8B6F47] transition-transform ${open?"rotate-180":""}`} />
      </button>
      {open && (
        <div
          className={`absolute z-[60] left-0 right-0 bg-white border border-[#E5D8C4] shadow-lg ${
            flipUp ? "bottom-full mb-1" : "top-full mt-1"
          }`}>
          <div className="relative border-b border-[#F3ECDF]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8B6F47]" />
            <input ref={inputRef} value={query}
              onChange={(e) => { setQuery(e.target.value); setHighlight(0); }}
              onKeyDown={onKey}
              placeholder="Type to search or press a letter..."
              data-testid={`${testId}-search`}
              className="w-full h-10 pl-9 pr-3 text-sm bg-transparent focus:outline-none" />
          </div>
          {/* Explicit maxHeight + overflow-y-auto on the list itself — using
              flex-1 with a max-height parent was flaky in some browsers and
              caused the list to render at full content height without a
              scrollbar. onWheel/onTouchMove just stopPropagation so scrolling
              inside the list never scrolls the modal/page underneath. */}
          <div ref={listRef} className="overflow-y-auto"
               style={{ maxHeight: "220px", overscrollBehavior: "contain" }}
               onWheel={(e) => e.stopPropagation()}
               onTouchMove={(e) => e.stopPropagation()}>
            {filtered.length === 0 && (
              <div className="p-3 text-xs text-[#A8998A]">{emptyMessage}</div>
            )}
            {filtered.map((o, i) => (
              <button type="button" key={o.value}
                ref={(el) => (optionRefs.current[i] = el)}
                onMouseEnter={() => setHighlight(i)}
                onClick={() => { onChange(o.value); setOpen(false); }}
                data-testid={`${testId}-option-${o.value}`}
                className={`w-full px-3 py-2 text-left text-sm flex items-center justify-between ${
                  i === highlight ? "bg-[#F3ECDF]" : "hover:bg-[#FAF7F2]"}`}>
                <div className="min-w-0">
                  <div className="text-[#2D2622] truncate">{o.label}</div>
                  {o.sub && <div className="text-[11px] text-[#8B6F47] font-mono2 mt-0.5 truncate">{o.sub}</div>}
                </div>
                {o.value === value && <Check size={14} className="text-[#2D2622] flex-shrink-0" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
